import {Observer, observer} from "mobx-react";
import React, { useEffect, useRef } from "react";
import {Section} from "controls/react/layout/section";
import {Toolbar} from "controls/react/layout/toolbar";
import {IconButton} from "controls/react/form";
import {AntTable, AntTableColumn, stringSorter, inputEditorRender} from "controls/react/ant/antTable";
import { ModelValidator, ValidationState } from "framework/mobx-integration";
import { ExclamationCircleOutlined } from '@ant-design/icons';
import './attributesEditor.less';

const i = require('core/localization/localization').translator({
	"Add new attribute": {
		"no": "Legg til ny attribut"
	},
	"Attributes": {
		"no": "Attributter"
	},
	"No attributes created": {
		"no": "Ingen attributter er opprettet"
	}
});

const b = require('b_').with('attributes-editor')

export interface IAttribute {
	key: string;
	value: string;
	validator?: ModelValidator<IAttribute>;
}

export type AttributesEditorProps<T extends IAttribute> = {
	onAdd?: () => void;
	onDelete?: (value: T) => void;
	value: T[];
	keyRender?: (value: string, record: T) => React.ReactNode;
	valueRender?: (value: string, record: T) => React.ReactNode;
	sortAttributes?: (value: T, rvalue: T, direction?: 'ascend' | 'descend') => number;
	className?: string;
}

export const AttributesEditor = observer(<T extends IAttribute>(props: AttributesEditorProps<T>) => {
	return <Section appearance={'frame'} containerClass={props.className} scrollable height={'full'}>
		<Toolbar title={i('Attributes')}>
			<IconButton iconName={'plus'}
						embedded={true}
						title={i('Add new attribute')}
						onClick={props.onAdd}/>
		</Toolbar>

		<AntTable dataSource={[...(props.value ?? [])]}
				  rowKey={'id'}
				  scroll={{y: 200}}
				  pagination={false}
				  locale={{emptyText:i('No attributes created')}}>
			<AntTableColumn<T> key={3} width={34}
											 render={(_, attr: T ) => <IconButton title={i('Delete')} iconName={'bin'} embedded={true} onClick={() => props.onDelete(attr)}/>}/>
			<AntTableColumn<T> title={i('Key')} dataIndex={'key'} key={'1'}
											 width={200}
											 sorter={props.sortAttributes || sortAttributes}
											 defaultSortOrder={"ascend"}
											 ellipsis={true}
											 render={props.keyRender || keyRender}
											 renderEditor={inputEditorRender}
			/>
			<AntTableColumn<T> title={i('Value')} dataIndex={'value'} key={'2'}
											 ellipsis={true}
											 render={props.valueRender || valueRender}
											 renderEditor={inputEditorRender}/>
		</AntTable>
	</Section>
});


function defaultRender(value: string, record: IAttribute, field: keyof IAttribute) {
	const validationField = record.validator?.getFieldValidator(field)
	const invalid = validationField && validationField.revalidated && validationField.validationState == ValidationState.Invalid
	const title = invalid ? record.validator?.getErrors(field).join('\n') : null
	return <div className={b(field, {invalid: invalid})} title={title}>
		{invalid && <ExclamationCircleOutlined className={b('invalid-icon')}/>}
		{value}
	</div>
}

function keyRender(value: string, record: IAttribute) {
	return defaultRender(value, record, 'key')
}

function valueRender(value: string, record: IAttribute) {
	return defaultRender(value, record, 'value')
}

function sortAttributes(lvalue: IAttribute, rvalue: IAttribute, direction: 'ascend' | 'descend') {
	return lvalue.key == null || lvalue.value == null
		? direction == 'ascend' ? -1 : 1
		: stringSorter(lvalue.key.toString(), rvalue.key?.toString())
}
